export const ATTRIBUTION_STORAGE_KEY = 'grok_attribution_v1';

const ATTRIBUTION_VERSION = 1;
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const DUPLICATE_TOUCH_WINDOW_MS = 30 * 60 * 1000;
const MAX_STORED_TOUCHES = 20;
const MAX_UTM_PARAMS = 20;
const MAX_VALUE_LENGTH = 500;
const UTM_KEY_PATTERN = /^utm_[a-z0-9_]+$/;

export interface AttributionTouch {
  touchId: string;
  capturedAt: number;
  landingPath: string;
  referrer?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmParams: Record<string, string>;
}

export interface AttributionState {
  version: typeof ATTRIBUTION_VERSION;
  touches: AttributionTouch[];
  syncedTouchIds: string[];
}

export function createAttributionTouch({
  capturedAt,
  referrer,
  touchId,
  url,
}: {
  capturedAt: number;
  referrer: string;
  touchId: string;
  url: URL;
}): AttributionTouch | null {
  const utmParams = getUtmParams(url.searchParams);
  const gclid = getBoundedValue(url.searchParams.get('gclid'));
  const gbraid = getBoundedValue(url.searchParams.get('gbraid'));
  const wbraid = getBoundedValue(url.searchParams.get('wbraid'));
  const normalizedReferrer = normalizeReferrer(referrer);

  if (!gclid && !gbraid && !wbraid && Object.keys(utmParams).length === 0) {
    return null;
  }

  return {
    touchId,
    capturedAt,
    landingPath: url.pathname.slice(0, 1000) || '/',
    ...(normalizedReferrer ? { referrer: normalizedReferrer } : {}),
    ...(gclid ? { gclid } : {}),
    ...(gbraid ? { gbraid } : {}),
    ...(wbraid ? { wbraid } : {}),
    utmParams,
  };
}

export function appendAttributionTouch(
  currentState: AttributionState | null,
  touch: AttributionTouch,
  now: number
) {
  const state = pruneAttributionState(currentState ?? emptyState(), now);
  if (isDuplicateTouch(state.touches, touch)) {
    return { added: false, state };
  }

  const touches = [...state.touches, touch].slice(-MAX_STORED_TOUCHES);
  const storedIds = new Set(touches.map((item) => item.touchId));
  return {
    added: true,
    state: {
      ...state,
      touches,
      syncedTouchIds: state.syncedTouchIds.filter((id) => storedIds.has(id)),
    },
  };
}

export function getPendingAttributionTouches(
  state: AttributionState,
  now = Date.now()
) {
  const current = pruneAttributionState(state, now);
  const syncedIds = new Set(current.syncedTouchIds);
  return current.touches.filter((touch) => !syncedIds.has(touch.touchId));
}

export function markAttributionTouchesSynced(
  state: AttributionState,
  touchIds: string[]
): AttributionState {
  const syncedTouchIds = new Set([...state.syncedTouchIds, ...touchIds]);
  return { ...state, syncedTouchIds: [...syncedTouchIds] };
}

export function readAttributionState(
  storage: Storage,
  now = Date.now()
): AttributionState {
  try {
    const rawValue = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!rawValue) return emptyState();
    return pruneAttributionState(
      parseAttributionState(JSON.parse(rawValue)),
      now
    );
  } catch {
    return emptyState();
  }
}

export function writeAttributionState(
  storage: Storage,
  state: AttributionState
) {
  try {
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Attribution must never affect navigation, sign-in, or generation.
  }
}

function emptyState(): AttributionState {
  return { version: ATTRIBUTION_VERSION, touches: [], syncedTouchIds: [] };
}

function getUtmParams(searchParams: URLSearchParams) {
  const entries: Array<[string, string]> = [];
  for (const [rawKey, rawValue] of searchParams) {
    const key = rawKey.trim().toLowerCase();
    const value = getBoundedValue(rawValue);
    if (!UTM_KEY_PATTERN.test(key) || !value) continue;
    entries.push([key.slice(0, 64), value]);
    if (entries.length >= MAX_UTM_PARAMS) break;
  }
  return Object.fromEntries(entries);
}

function getBoundedValue(value: string | null) {
  const normalized = value?.trim();
  return normalized ? normalized.slice(0, MAX_VALUE_LENGTH) : undefined;
}

function normalizeReferrer(value: string) {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`.slice(0, 1000);
  } catch {
    return undefined;
  }
}

function parseAttributionState(value: unknown): AttributionState {
  if (!value || typeof value !== 'object') return emptyState();
  const candidate = value as Partial<AttributionState>;
  if (candidate.version !== ATTRIBUTION_VERSION) return emptyState();
  if (!Array.isArray(candidate.touches)) return emptyState();
  if (!Array.isArray(candidate.syncedTouchIds)) return emptyState();
  return {
    version: ATTRIBUTION_VERSION,
    touches: candidate.touches.filter(isAttributionTouch),
    syncedTouchIds: candidate.syncedTouchIds.filter(
      (id): id is string => typeof id === 'string'
    ),
  };
}

function isAttributionTouch(value: unknown): value is AttributionTouch {
  if (!value || typeof value !== 'object') return false;
  const touch = value as Partial<AttributionTouch>;
  const utmEntries =
    touch.utmParams && typeof touch.utmParams === 'object'
      ? Object.entries(touch.utmParams)
      : [];
  return (
    typeof touch.touchId === 'string' &&
    touch.touchId.length > 0 &&
    touch.touchId.length <= 64 &&
    typeof touch.capturedAt === 'number' &&
    Number.isInteger(touch.capturedAt) &&
    touch.capturedAt > 0 &&
    typeof touch.landingPath === 'string' &&
    touch.landingPath.startsWith('/') &&
    touch.landingPath.length <= 1000 &&
    isOptionalReferrer(touch.referrer) &&
    isOptionalAttributionValue(touch.gclid) &&
    isOptionalAttributionValue(touch.gbraid) &&
    isOptionalAttributionValue(touch.wbraid) &&
    utmEntries.length <= MAX_UTM_PARAMS &&
    utmEntries.every(
      ([key, entryValue]) =>
        key.length <= 64 &&
        UTM_KEY_PATTERN.test(key) &&
        typeof entryValue === 'string' &&
        entryValue.length <= MAX_VALUE_LENGTH
    )
  );
}

function isOptionalReferrer(value: unknown) {
  if (value === undefined) return true;
  if (typeof value !== 'string' || value.length > 1000) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function isOptionalAttributionValue(value: unknown) {
  return (
    value === undefined ||
    (typeof value === 'string' &&
      value.trim().length > 0 &&
      value.trim().length <= MAX_VALUE_LENGTH)
  );
}

function pruneAttributionState(
  state: AttributionState,
  now: number
): AttributionState {
  const earliest = now - ATTRIBUTION_TTL_MS;
  const touches = state.touches
    .filter((touch) => touch.capturedAt >= earliest && touch.capturedAt <= now)
    .slice(-MAX_STORED_TOUCHES);
  const storedIds = new Set(touches.map((touch) => touch.touchId));
  return {
    ...state,
    touches,
    syncedTouchIds: state.syncedTouchIds.filter((id) => storedIds.has(id)),
  };
}

function isDuplicateTouch(
  existingTouches: AttributionTouch[],
  touch: AttributionTouch
) {
  return existingTouches.some((existing) => {
    if (hasMatchingClickId(existing, touch)) return true;
    const age = touch.capturedAt - existing.capturedAt;
    return (
      age >= 0 &&
      age <= DUPLICATE_TOUCH_WINDOW_MS &&
      getTouchFingerprint(existing) === getTouchFingerprint(touch)
    );
  });
}

function hasMatchingClickId(
  existing: AttributionTouch,
  touch: AttributionTouch
) {
  return Boolean(
    (touch.gclid && touch.gclid === existing.gclid) ||
      (touch.gbraid && touch.gbraid === existing.gbraid) ||
      (touch.wbraid && touch.wbraid === existing.wbraid)
  );
}

function getTouchFingerprint(touch: AttributionTouch) {
  const utmEntries = Object.entries(touch.utmParams).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  return JSON.stringify([
    touch.landingPath,
    touch.gclid,
    touch.gbraid,
    touch.wbraid,
    utmEntries,
  ]);
}
